// app/javascript/super_admin/manage/project/ProjectTranslateToEnglish.js

import React, { useContext, useEffect, useState, useRef } from "react";
import { ProjectContext } from "../../context/project_context";
import TimecodeInput from "../../components/TimecodeInput";
import { countDuplicateEnglishTranscripts } from "../../utils/english_transcript_duplicates";

const ProjectTranslateToEnglish = () => {
  const {
    project,
    project_id,
    org_id,
    updateProject,
    english_transcripts, 
    fetchEnglishTranscripts,
    translateSingleTranscript,
    translateAllTranscripts,
    updateEnglishTranscriptTimecodes,
    removeDuplicateEnglishTranscripts,
    // Media playback
    activeTranscriptId,
    autoScrollEnabled,
    seekToTranscript
  } = useContext(ProjectContext);
  
  const [loadingStates, setLoadingStates] = useState({});
  const [loading, setLoading] = useState(false);
  const [addedPrompt, setAddedPrompt] = useState('');
  const [savingStates, setSavingStates] = useState({});
  const [isPromptExpanded, setIsPromptExpanded] = useState(true);

  // Refs for auto-scrolling
  const rowRefs = useRef({});

  useEffect(() => {
    fetchEnglishTranscripts();
  }, []);

  // Auto-scroll to active row
  useEffect(() => {
    if (autoScrollEnabled && activeTranscriptId && rowRefs.current[activeTranscriptId]) {
      rowRefs.current[activeTranscriptId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeTranscriptId, autoScrollEnabled]);

  // Listen for focus active transcript event (Shift+Enter shortcut)
  useEffect(() => {
    const handleFocusActive = () => {
      if (activeTranscriptId && rowRefs.current[activeTranscriptId]) {
        const row = rowRefs.current[activeTranscriptId];
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus the translate button in the row
        const button = row.querySelector('button.btn-primary');
        if (button) {
          button.focus();
        }
      }
    };

    window.addEventListener('focusActiveTranscript', handleFocusActive);
    return () => window.removeEventListener('focusActiveTranscript', handleFocusActive);
  }, [activeTranscriptId]);

  // Listen for seek to focused transcript event (Ctrl+Enter shortcut)
  useEffect(() => {
    const handleSeekToFocused = () => {
      // Find which row's element is currently focused
      const focusedElement = document.activeElement;
      const row = focusedElement?.closest('tr');
      if (row) {
        const transcriptId = Object.keys(rowRefs.current).find(
          id => rowRefs.current[id] === row
        );
        if (transcriptId) {
          const transcript = english_transcripts.find(t => t.id === parseInt(transcriptId));
          if (transcript) {
            seekToTranscript(transcript);
          }
        }
      }
    };

    window.addEventListener('seekToFocusedTranscript', handleSeekToFocused);
    return () => window.removeEventListener('seekToFocusedTranscript', handleSeekToFocused);
  }, [english_transcripts, seekToTranscript]);

  const handleTranslateClick = async (transcript, index) => {
    setLoadingStates(prev => ({ ...prev, [index]: true }));
    try {
      await translateSingleTranscript(transcript, addedPrompt);
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleTimecodeChange = async (transcriptId, field, newValue) => {
    const transcript = english_transcripts.find(t => t.id === transcriptId);
    if (!transcript) return;

    const startTime = field === 'start_time' ? newValue : parseFloat(transcript.start_time);
    const endTime = field === 'end_time' ? newValue : parseFloat(transcript.end_time);

    if (endTime <= startTime) {
      alert('End time must be greater than start time');
      return;
    }

    try {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: true }));
      await updateEnglishTranscriptTimecodes(transcriptId, startTime, endTime);
    } catch (error) {
      console.error('Failed to update timecodes:', error);
      alert('Failed to update timecodes');
    } finally {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: false }));
    }
  };

  const handleBack = async () => {
    await updateProject({
      ...project,
      project_status: "sentence_thought_fixing"
    });
  };

  const handleComplete = async () => {
    await updateProject({
      ...project,
      project_status: "correct_english"
    });
  };

  const handleTranslateAll = async () => {
    setLoading(true);
    await translateAllTranscripts(addedPrompt);
    setLoading(false);
  };

  const handleRowClick = (transcript) => {
    seekToTranscript(transcript);
  };

  const duplicateCount = countDuplicateEnglishTranscripts(english_transcripts);

  const handleRemoveDuplicates = async () => {
    if (duplicateCount === 0) return;
    if (!window.confirm(
      `Remove ${duplicateCount} duplicate row(s)? Rows with the same timeframe and original text will be deleted. The earliest row in each group will be kept.`
    )) return;
    await removeDuplicateEnglishTranscripts();
  };

  return (
    <div className="sentence-thought-fixing">
      <div className="d-flex justify-content-between align-items-center">
        <h2>English Transcripts Translation</h2>
        <div className="d-flex gap-2 mt-3">
          <a href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/taglish_srt_text`} className="btn btn-primary" target="_blank" download={`${project.name}_taglish_srt.srt`}>
            Download Taglish SRT
          </a>
          <button className="btn btn-primary" onClick={handleTranslateAll}>Translate All</button>
        </div>
      </div>
      <div
        style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          width: '300px',
          backgroundColor: '#ffd2fa',
          padding: '0',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          border: '1px solid #dee2e6',
          overflow: 'hidden'
        }}
      >
        <div 
          onClick={() => setIsPromptExpanded(!isPromptExpanded)}
          style={{ 
            padding: '10px 15px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f5c6ef',
            borderBottom: isPromptExpanded ? '1px solid #dee2e6' : 'none'
          }}
        >
          <label className="form-label" style={{ fontWeight: 'bold', marginBottom: '0', cursor: 'pointer' }}>
            Additional Prompt (optional):
          </label>
          <span style={{ fontSize: '14px', transition: 'transform 0.2s', transform: isPromptExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            ▼
          </span>
        </div>
        {isPromptExpanded && (
          <div style={{ padding: '15px' }}>
            <textarea
              id="added-prompt"
              className="form-control"
              rows="3"
              placeholder="Enter additional instructions for translation..."
              value={addedPrompt}
              onChange={(e) => setAddedPrompt(e.target.value)}
              style={{ fontSize: '12px', resize: 'vertical' }}
            />
            <small className="form-text text-muted" style={{ fontSize: '11px' }}>
              This prompt will be added to all translation requests.
            </small>
          </div>
        )}
      </div>
      <div className="transcripts-list mt-3">
        <div className="d-flex gap-2 mb-2">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={handleRemoveDuplicates}
            disabled={duplicateCount === 0}
          >
            Remove Duplicates ({duplicateCount})
          </button>
        </div>
        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>T#</th>
              <th style={{ width: '240px' }}>Timecodes</th>
              <th>Original Text</th>
              <th>Translated Text</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {english_transcripts.map((transcript, index) => {
              const isActive = transcript.id === activeTranscriptId;
              
              return (
                <tr 
                  key={transcript.id}
                  ref={el => rowRefs.current[transcript.id] = el}
                  className={isActive ? 'table-info' : ''}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <td 
                    style={{ width: '50px' }}
                    onClick={() => handleRowClick(transcript)}
                  >
                    {transcript.sequence_number}
                  </td>
                  <td style={{ width: '240px' }}>
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted" style={{ width: '35px' }}>Start:</small>
                        <TimecodeInput
                          value={parseFloat(transcript.start_time)}
                          onChange={(val) => handleTimecodeChange(transcript.id, 'start_time', val)}
                        />
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted" style={{ width: '35px' }}>End:</small>
                        <TimecodeInput
                          value={parseFloat(transcript.end_time)}
                          onChange={(val) => handleTimecodeChange(transcript.id, 'end_time', val)}
                        />
                      </div>
                      {savingStates[`timecode_${transcript.id}`] && (
                        <small className="text-muted">Saving...</small>
                      )}
                    </div>
                  </td>
                  <td className="p-1">{transcript.original_text}</td>
                  <td className="p-1">{transcript.text || '...'}</td>
                  <td>
                    <button
                      onClick={() => handleTranslateClick(transcript, index)}
                      disabled={loadingStates[index]}
                      className="btn btn-primary btn-sm"
                    >
                      {loadingStates[index] ? 'Translating...' : 'Translate'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex gap-2 mt-3">
        <button 
          className="btn btn-secondary"
          onClick={handleBack}
        >
          Back to Sentence Building
        </button>
        <button
          className="btn btn-primary"
          onClick={handleComplete}
          disabled={loading}
        >
          Complete English Translation
        </button>
      </div>
    </div>
  );
};

export default ProjectTranslateToEnglish;
